import { useTranslation } from "react-i18next";
import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Dialog, Menu, Modal, ModalOverlay } from "react-aria-components";
import { ChevronDown } from "lucide-react";
import type { ManualState } from "@arc/contracts";
import type { AuthSession } from "../../../auth/keycloak.js";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Popover, PopoverTrigger } from "@/components/ui/popover";
import { DropdownMenuItem } from "@/components/ui/dropdown-menu";
import { ManualStateBadge } from "../ManualStateBadge.js";
import type { ManualBody } from "@arc/contracts";
import { availableManualTransitions, type ManualTransitionKey } from "../manual-transitions.js";
import { useManualCheckGate } from "./useManualCheckGate.js";
import {
  invalidateManualBody,
  useConfirmManual,
  useMarkManual,
  useReturnManualToDraft,
  useSubmitReviewManual,
  useUnmarkManual,
} from "../useManualMutations.js";

/**
 * Status-while-editing (ADR-002 Phase 10) — the review state machine driven from INSIDE the authoring editor, reusing the
 * SAME machine as the manuals-list kebab + drawer stepper ({@link availableManualTransitions} + the shipped transition
 * hooks), so there is no parallel status system. It shows the current {@link ManualStateBadge} + a tier-filtered "Change
 * status" menu; each transition carries `expectedFrom: state` (the ADR-018 compare-and-set — a 409 means it moved under us).
 *
 * On success it invalidates the manual BODY (not just the lists — the shipped hooks omit the body key), so the editor
 * re-derives read-only reactively off the refetched `manual.state`: a transition OFF `draft` drops the surface to preview,
 * and `return`-to-draft reopens it. `mark` collects a comment first (the only transition that needs one).
 */
export function EditorStatusControl({
  session,
  manual,
  canManage,
  canConfirm,
  onBeforeAction,
  body,
}: {
  session: AuthSession | null;
  manual: { id: number; state: ManualState; markedComment: string | null };
  canManage: boolean;
  canConfirm: boolean;
  /** Awaited before firing a transition — the editor flushes (and awaits) pending autosaves so a `draft → ready_for_review`
   *  can't race/drop an in-flight text edit (panel High + r2). */
  onBeforeAction?: () => void | Promise<void>;
  /** The loaded body, for the Check Manual gate (ADR-018 addendum §4). The editor already holds it, so the gate is a
   *  pure function over it — no round-trip. Omitted ⇒ no gate (the transition fires straight through), which is what
   *  a caller without a body should get rather than a dialog it cannot fill in. */
  body?: ManualBody;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [markOpen, setMarkOpen] = useState(false);
  /** The Check Manual gate (§4 D2 gates submit AND confirm), shared with the manuals list's door — see
   *  {@link useManualCheckGate}. This door's freshest body is the cache, read at fire time below. */
  const { gate, dialog: checkDialog } = useManualCheckGate();

  const submit = useSubmitReviewManual(session);
  const confirm = useConfirmManual(session);
  const unmark = useUnmarkManual(session);
  const toDraft = useReturnManualToDraft(session);
  const mark = useMarkManual(session);

  const transitions = availableManualTransitions(manual.state, { canManage, canConfirm });
  const busy = submit.isPending || confirm.isPending || unmark.isPending || toDraft.isPending || mark.isPending;
  // After ANY transition, force the body to refetch so the editor's read-only derivation tracks the new state.
  const afterTransition = { onSuccess: () => invalidateManualBody(qc, session, manual.id) };

  const fire = async (key: ManualTransitionKey) => {
    // AWAIT pending step-text autosaves so they land BEFORE the manual leaves an editable state. If a save FAILS (conflict),
    // BLOCK the transition — the editor's Refresh banner surfaces it — rather than moving the manual with an unsaved edit
    // (panel #210 High #2).
    try {
      await onBeforeAction?.();
    } catch {
      return;
    }
    // The PUBLISH direction goes through Check Manual first (§4 D2). Backward moves — return-to-draft, unmark — are
    // not gated: the legacy gates neither, and there is nothing to validate about un-publishing. The dialog opens
    // whether or not anything failed; it is a summary, and one that only appears on failure trains people to dismiss
    // it. Without a body there is no gate — firing straight through beats a dialog with nothing in it.
    if (key === "submit" || key === "confirm") {
      // Read the body back from the CACHE, not the captured prop. `fire` closes over `body` at render time, and the
      // flush above is precisely what changes it: the autosave's `onSuccess` seeds the fresh authoritative body with
      // `setQueryData` on this key. Checking the closure's copy would summarise PRE-flush state on the fast
      // edit-then-submit path — a false pass on a step just emptied, a false warning on one just filled — which is
      // the exact path `flushAll` exists to cover (diff panel, consensus). `setQueryData` is synchronous, so this
      // sees the new value whether or not React has re-rendered yet.
      const fresh = qc.getQueryData<ManualBody>(["manual-body", session?.realm, session?.sub, manual.id]) ?? body;
      gate(fresh, key, () => run(key));
      return;
    }
    run(key);
  };

  const run = (key: ManualTransitionKey) => {
    const vars = { id: manual.id, expectedFrom: manual.state };
    if (key === "submit") submit.mutate(vars, afterTransition);
    else if (key === "confirm") confirm.mutate(vars, afterTransition);
    else if (key === "unmark") unmark.mutate(vars, afterTransition);
    else if (key === "return") toDraft.mutate(vars, afterTransition);
    else if (key === "mark") setMarkOpen(true);
  };

  return (
    <>
      <PopoverTrigger isOpen={open} onOpenChange={setOpen}>
        <Button variant="outline" size="sm" isDisabled={busy || transitions.length === 0} className="[&>span:first-child]:bg-transparent [&>span:first-child]:p-0 dark:border-border gap-1.5">
          <ManualStateBadge state={manual.state} comment={manual.markedComment} />
          {transitions.length > 0 ? <ChevronDown aria-hidden="true" className="size-3.5 opacity-70" /> : null}
        </Button>
        <Popover className="w-56 gap-0 p-0" placement="bottom end">
          <Dialog aria-label={t("manualEditor.changeStatus")} className="outline-none">
            <div className="border-border border-b px-2.5 py-2 text-xs font-medium text-muted-foreground">{t("manualEditor.changeStatusTitle")}</div>
            <Menu
              aria-label={t("manualEditor.statusTransitions")}
              className="flex flex-col p-1 outline-none"
              onAction={(key) => {
                setOpen(false);
                void fire(key as ManualTransitionKey);
              }}
            >
              {transitions.map((tr) => {
                const Icon = tr.icon;
                return (
                  <DropdownMenuItem key={tr.key} id={tr.key} textValue={t(tr.labelKey)} variant={tr.destructive ? "destructive" : "default"}>
                    <Icon />
                    {t(tr.labelKey)}
                  </DropdownMenuItem>
                );
              })}
            </Menu>
          </Dialog>
        </Popover>
      </PopoverTrigger>

      {checkDialog}
      {markOpen ? (
        <MarkCommentDialog
          pending={mark.isPending}
          onCancel={() => setMarkOpen(false)}
          onSubmit={(comment) =>
            mark.mutate(
              { id: manual.id, expectedFrom: manual.state, comment },
              { onSuccess: () => { invalidateManualBody(qc, session, manual.id); setMarkOpen(false); } },
            )
          }
        />
      ) : null}
    </>
  );
}

/** The comment prompt for `confirmed → marked` (the one transition that needs a reason). A minimal focus-trapped modal
 *  over the same react-aria primitives as the reader's overlays; submit is gated on a non-empty trimmed comment. */
function MarkCommentDialog({
  pending,
  onCancel,
  onSubmit,
}: {
  pending: boolean;
  onCancel: () => void;
  onSubmit: (comment: string) => void;
}) {
  const { t } = useTranslation();
  const [comment, setComment] = useState("");
  const trimmed = comment.trim();
  return (
    <ModalOverlay isDismissable isOpen onOpenChange={(o) => !o && onCancel()} className="fixed inset-0 z-[60] bg-black/40 backdrop-blur-sm">
      <Modal className="fixed top-1/2 left-1/2 w-[min(28rem,calc(100%-2rem))] -translate-x-1/2 -translate-y-1/2 outline-hidden">
        <Dialog aria-label={t("manualEditor.markAsFaulty")} className="border-border bg-background rounded-xl border p-5 shadow-xl outline-none">
          <h2 className="text-base font-semibold">{t("manualEditor.markAsFaulty")}</h2>
          <p className="text-muted-foreground mt-1 text-sm">{t("manualEditor.markReasonHint")}</p>
          <Textarea
            aria-label={t("manualEditor.reason")}
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            rows={4}
            maxLength={1000}
            placeholder={t("manualEditor.whatsWrongWithThisManual")}
            className="mt-3"
          />
          <div className="mt-4 flex justify-end gap-2">
            <Button variant="ghost" size="sm" onPress={onCancel} isDisabled={pending}>
              {t("manualEditor.cancel")}
            </Button>
            <Button variant="destructive" size="sm" onPress={() => onSubmit(trimmed)} isDisabled={pending || trimmed.length === 0}>
              {t("manualEditor.markAsFaulty")}
            </Button>
          </div>
        </Dialog>
      </Modal>
    </ModalOverlay>
  );
}
